// @ts-expect-error Symbol.isTuple is usually not assignable
if (!Symbol.isTuple)
    Symbol.isTuple = Symbol("isTuple");
const supportsWeak = typeof WeakRef !== "undefined";
const finalizer = supportsWeak &&
    new FinalizationRegistry(({ tuple, generation }) => {
        (function cleanup(current = cache, path = tuple) {
            if (path.length === 0 && generation === current.generation)
                return (current.value = null);
            const key = path.shift();
            const nextCache = current.get(key);
            if (!nextCache)
                return;
            cleanup(nextCache, path);
            if (!nextCache.value && nextCache.size === 0)
                current.delete(key);
        })();
    });
const createCache = () => Object.assign(new Map(), {
    value: null,
    generation: 0,
});
const cache = createCache();
const tuples = supportsWeak ? new WeakSet() : new Set();
function Tuple(...items) {
    return Tuple.from(items);
}
Tuple.from = (items) => {
    let current = cache;
    let i = 0;
    for (; i < items.length; ++i) {
        const item = items[i];
        if (!current.has(item))
            current.set(item, createCache());
        current = current.get(item);
    }
    let { value } = current;
    if (value && "deref" in value)
        value = value.deref();
    if (value)
        return value;
    const tuple = Object.freeze(Object.assign(items, { [Symbol.isTuple]: true }));
    current.value = supportsWeak ? new WeakRef(tuple) : tuple;
    if (finalizer)
        finalizer.register(tuple, {
            tuple: [...tuple],
            generation: ++current.generation,
        });
    tuples.add(tuple);
    return tuple;
};
Tuple.isTuple = (maybeTuple) => tuples.has(maybeTuple);

// @ts-expect-error Symbol.isRecord is usually not assignable
if (!Symbol.isRecord)
    Symbol.isRecord = Symbol("isRecord");
const tuplesByRecord = supportsWeak
    ? new WeakMap()
    : new Map();
const recordsByTuple = supportsWeak
    ? new WeakMap()
    : new Map();
const symbolKeyError = new TypeError("A Symbol cannot be used as a property key in a Record.");
const nonRecordError = new TypeError("Record.entries received a non-record.");
function Record(obj) {
    if (Object.getOwnPropertySymbols(obj).length > 0)
        throw symbolKeyError;
    return Record.fromEntries(Object.entries(obj));
}
Record.entries = (record) => {
    const tuple = tuplesByRecord.get(record);
    if (!tuple)
        throw nonRecordError;
    return tuple;
};
Record.fromEntries = (entries) => {
    // @ts-expect-error `entries` is not necessarily a tuple
    if (recordsByTuple.has(entries))
        return recordsByTuple.get(entries);
    const tuple = Tuple.from([...entries]
        .sort(([a], [b]) => a.localeCompare(b))
        .map((entry) => {
        if (typeof entry[0] === "symbol")
            throw symbolKeyError;
        return Tuple.from(entry);
    }));
    if (!recordsByTuple.has(tuple)) {
        const record = Object.freeze(Object.assign(Object.fromEntries(tuple), {
            [Symbol.isRecord]: true,
        }));
        recordsByTuple.set(tuple, record);
        tuplesByRecord.set(record, tuple);
    }
    return recordsByTuple.get(tuple);
};
Record.isRecord = (maybeRecord) => tuplesByRecord.has(maybeRecord);

function RecordTuple(input) {
    return (Array.isArray(input) ? Tuple.from(input) : Record(input));
}
RecordTuple.deep = (input) => {
    if (!input || typeof input !== "object")
        throw new Error(`RecordTuple.deep: Invalid input ${input}`);
    if (Tuple.isTuple(input) || Record.isRecord(input))
        return input;
    if (Array.isArray(input))
        return Tuple.from(input.map((item) => item && (typeof item === "object" ? RecordTuple.deep(item) : item)));
    return Record.fromEntries(Object.entries(input).map(([k, v]) => [
        k,
        v && (typeof v === "object" ? RecordTuple.deep(v) : v),
    ]));
};

export { Record, RecordTuple, Tuple };
